/**
 * ReasoningPath - Displays step-by-step reasoning progress
 *
 * Shows numbered steps with completion status and overall status badge.
 */

import clsx from 'clsx'
import { CheckCircle, Circle, AlertCircle } from 'lucide-react'
import { useTranslation } from '@/i18n'
import type { ReasoningPath as ReasoningPathType, ReasoningPathStatus } from '../../types'
import { dedupReasoningSteps } from '../../utils/dedupReasoningSteps'

interface ReasoningPathProps {
  reasoningPath: ReasoningPathType
}

function getStatusConfig(
  status: ReasoningPathStatus,
  t: (key: string, opts: Record<string, unknown>) => string,
) {
  switch (status) {
    case 'completed':
      return {
        label: t('chatv2:reasoning.status_completed', { defaultValue: 'Completed' }),
        color: 'text-green-600 dark:text-green-400',
        bgColor: 'bg-green-50 dark:bg-green-900/20',
      }
    case 'in_progress':
      return {
        label: t('chatv2:reasoning.status_in_progress', { defaultValue: 'In Progress' }),
        color: 'text-blue-600 dark:text-blue-400',
        bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      }
    case 'cancelled':
      return {
        label: t('chatv2:reasoning.status_cancelled', { defaultValue: 'Cancelled' }),
        color: 'text-gray-600 dark:text-gray-400',
        bgColor: 'bg-gray-50 dark:bg-dark-muted',
      }
    case 'error':
      return {
        label: t('chatv2:reasoning.status_error', { defaultValue: 'Unable to find an answer' }),
        color: 'text-gray-600 dark:text-gray-400',
        bgColor: 'bg-gray-100 dark:bg-dark-accent',
      }
    default:
      return {
        label: t('chatv2:reasoning.status_unknown', { defaultValue: 'Unknown' }),
        color: 'text-gray-600 dark:text-gray-400',
        bgColor: 'bg-gray-50 dark:bg-dark-muted',
      }
  }
}

export function ReasoningPath({ reasoningPath }: ReasoningPathProps) {
  const { t } = useTranslation()
  const { steps: rawSteps, status } = reasoningPath
  const steps = dedupReasoningSteps(rawSteps)
  const statusConfig = getStatusConfig(status, t)

  return (
    <div className="rounded-lg p-3 mb-3 border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-muted">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
          {status === 'in_progress' && (
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
          )}
          {status === 'completed' && (
            <CheckCircle className="h-4 w-4 text-brand dark:text-brand-light" />
          )}
          {status === 'error' && (
            <AlertCircle className="h-4 w-4 text-gray-500 dark:text-dark-text-muted" />
          )}
          {t('chatv2:messages.thinking_short', { defaultValue: 'Thinking' })}
        </h4>

        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-dark-text-muted">
          <span
            className={clsx(
              'px-2 py-1 rounded-full text-xs font-medium',
              statusConfig.bgColor,
              statusConfig.color
            )}
          >
            {statusConfig.label}
          </span>
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-2">
        {steps.map((step) => (
          <div key={step.step} className="flex items-center gap-3">
            {/* Step icon */}
            <div className="flex-shrink-0">
              {step.completed ? (
                <CheckCircle className="h-4 w-4 text-brand dark:text-brand-light" />
              ) : status === 'in_progress' && step.step === steps.filter(s => s.completed).length + 1 ? (
                <Circle className="h-4 w-4 text-brand dark:text-brand-light animate-pulse" />
              ) : (
                <Circle className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              )}
            </div>

            {/* Step content */}
            <div className="flex-1 flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {step.step}.
              </span>
              <span
                className={clsx(
                  'text-sm',
                  step.completed ? 'text-gray-700 dark:text-gray-300' : 'text-gray-500 dark:text-dark-text-muted'
                )}
              >
                {step.description}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default ReasoningPath
